import React, {Fragment} from "react";
import Error from "../../utils/alert/error/Error";
import GeolocationExplanation from "../../utils/alert/explanation/GeolocationExplanation";
import GeolocateDevice from "../../hardware/GeolocateDevice";
import ValidationButton from "../../utils/buttons/ValidationButton";
import GPSCoordinates from "../../../models/GPSCoordinates";
import DisplayCurrentGeolocation from "./DisplayCurrentGeolocation";
import NoData from "../../utils/alert/error/NoData";
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {WithTranslation, withTranslation} from "react-i18next";

/**
* Interface inheriting the translation module.
* We put all the props of the component there.
**/
interface Props extends WithTranslation {
    /**
     * Function called when the user clicks on the validate button while his location is valid.
     * @param {GPSCoordinates} location User's position.
     */
    onValidate: (location: GPSCoordinates) => void,
    /**
     * Allows you to display the coordinates on the map if they already exist.
     */
    currentLocation?: GPSCoordinates,
    /**
     * Component container class name.
     */
    className?: string,
}

/**
 * Component that allows a user to locate their device and view its position on a map.
 * A validation button is available.
 * @author Alizée Grand
 * @version 1.0
 * @creation 28/03/2022
 * @last_update 25/04/2022
 **/
class GeolocationStep extends React.Component<Props> {

    state = {
        location: this.props.currentLocation,
        isError: false,
        isAlertOpen: true
    }

    render() {

        /**
* Retrieves from the props the function to access the translation.
**/
const getTranslation = this.props.t;

        const handleClose = () => {
            this.setState({isAlertOpen: false});
        };

        /**
         * Checks if the GPS data stored in the component is valid.
         * @return {boolean} True if they are valid, false otherwise.
         */
        const isLocationCorrect = (): boolean => {
            return (
                this.state.location !== undefined &&
                this.state.location.isValid()
            );
        }

        /**
         * Function called when the user clicks on the validate button.
         * Calls the corresponding function passed in props if the location is valid, otherwise manages the error display.
         */
        const onValidate = () => {
            if(isLocationCorrect()) {
                this.setState({isError: false});
                this.props.onValidate(this.state.location as GPSCoordinates);
            } else {
                this.setState({isError: true});
            }
        }

        /**
         * Function called when the user clicks on the locate button.
         * Store the GPS coordinates of the user in the component.
         * @param {GPSCoordinates} locate GPS coordinates of the user.
         */
        const onLocate = (locate: GPSCoordinates) => {
            this.setState({location: locate, isError: false});
        }

        return (
            <Fragment>
                <Dialog
                    open={this.state.isAlertOpen}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">
                        {getTranslation("steps.geolocation.dialog.title")}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            {getTranslation("steps.geolocation.dialog.content")}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>
                            {getTranslation("steps.geolocation.dialog.accept")}
                        </Button>
                    </DialogActions>
                </Dialog>

                <div className="step-main-container">
                    <GeolocationExplanation/>
                    {
                        this.state.isError ? (
                            <Error>
                                {getTranslation("steps.geolocation.error")}
                            </Error>
                        ) : ""
                    }

                    <div className="form-container">
                        <GeolocateDevice onLocate={onLocate}/>
                        {
                            this.state.location ? (
                                <div style={{width: '100%', height: '550px', marginTop: '2vh'}}>
                                    <DisplayCurrentGeolocation location={this.state.location}/>
                                </div>
                            ) : (
                                <NoData>
                                    {getTranslation("steps.geolocation.no-location")}
                                </NoData>
                            )
                        }
                    </div>
                    <div className="validation-container">
                        <ValidationButton onClick={onValidate}/>
                    </div>
                </div>
            </Fragment>

        );

    };

}

export default withTranslation()(GeolocationStep);
